import { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../../App";
import axios from "axios";
import {
    Box,
    Typography,
    Button,
    Alert,
    Snackbar,
    CircularProgress,
    Paper,
    Card,
    CardActionArea,
    CardContent,
    Fade,
    Chip
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import SpeedIcon from "@mui/icons-material/Speed";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SaveIcon from "@mui/icons-material/Save";
import styles from "./LeaderboardController.module.css";

type LeaderboardMode =
    | "leaderboardSegueLinha"
    | "leaderboardVelocidade"
    | "leaderboardFinal"
    | "sponsors";

const LeaderboardController = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const [selectedMode, setSelectedMode] = useState<LeaderboardMode>("leaderboardSegueLinha");
    const [currentDisplayedMode, setCurrentDisplayedMode] = useState<LeaderboardMode>("leaderboardSegueLinha");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error",
    });

    // Fetch current leaderboard mode when component mounts
    useEffect(() => {
        const fetchCurrentMode = async () => {
            setLoading(true);
            try {
                const response = await axios.get("/leaderboard/mode");
                setSelectedMode(response.data.mode);
                setCurrentDisplayedMode(response.data.mode);
            } catch (error) {
                console.error("Failed to fetch leaderboard mode:", error);
                setSnackbar({
                    open: true,
                    message: "Falha ao carregar modo atual do leaderboard",
                    severity: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCurrentMode();
    }, []);

    // Handler to save the selected mode
    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post("/leaderboard/mode", { mode: selectedMode });
            setCurrentDisplayedMode(selectedMode);
            setSnackbar({
                open: true,
                message: "Modo do leaderboard atualizado com sucesso!",
                severity: "success",
            });
        } catch (error) {
            console.error("Failed to update leaderboard mode:", error);
            setSnackbar({
                open: true,
                message: "Falha ao atualizar modo do leaderboard",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    // Get icon for leaderboard mode
    const getModeIcon = (mode: LeaderboardMode) => {
        switch (mode) {
            case "leaderboardSegueLinha":
                return <ViewListIcon fontSize="large" />;
            case "leaderboardVelocidade":
                return <SpeedIcon fontSize="large" />;
            case "leaderboardFinal":
                return <EmojiEventsIcon fontSize="large" />;
            case "sponsors":
                return <HandshakeIcon fontSize="large" />;
        }
    };

    // Get display name for leaderboard mode
    const getModeName = (mode: LeaderboardMode) => {
        switch (mode) {
            case "leaderboardSegueLinha":
                return "Leaderboard Segue Linha";
            case "leaderboardVelocidade":
                return "Leaderboard Velocidade";
            case "leaderboardFinal":
                return "Leaderboard Final";
            case "sponsors":
                return "Grid de Patrocinadores";
        }
    };

    // Get mode description
    const getModeDescription = (mode: LeaderboardMode) => {
        switch (mode) {
            case "leaderboardSegueLinha":
                return "Exibe os resultados da prova de seguir linha";
            case "leaderboardVelocidade":
                return "Exibe os resultados da prova de velocidade";
            case "leaderboardFinal":
                return "Exibe a classificação final com pontos combinados";
            case "sponsors":
                return "Exibe uma grade rotativa de logotipos dos patrocinadores";
        }
    };

    return (
        <Box className={`${styles.container} ${isDarkMode ? styles.darkTheme : styles.lightTheme}`}>
            <Paper
                elevation={3}
                className={styles.pageHeader}
                sx={{
                    backgroundImage: "linear-gradient(135deg, #0077b6, #023e8a)",
                    mb: 4,
                }}>
                <Typography variant="h4" className={styles.title}>
                    Controle do Leaderboard
                </Typography>
                <Typography variant="subtitle1" className={styles.subtitle}>
                    Gerenciar o que é exibido no leaderboard público
                </Typography>
            </Paper>

            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress size={60} />
                </Box>
            ) : (
                <Fade in={!loading}>
                    <Box>
                        <Paper elevation={3} className={styles.currentModeCard} sx={{ mb: 4 }}>
                            <Box className={styles.statusHeader}>
                                <Typography variant="h6">Modo atual em exibição</Typography>
                                <Chip 
                                    label="Ativo" 
                                    color="success" 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                            
                            <Box className={styles.currentModeDisplay}>
                                <Box className={styles.iconWrapper}>
                                    {getModeIcon(currentDisplayedMode)}
                                </Box>
                                <Box>
                                    <Typography variant="h5" className={styles.currentModeName}>
                                        {getModeName(currentDisplayedMode)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {getModeDescription(currentDisplayedMode)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                            Selecione um modo para exibição
                        </Typography>

                        <Box className={styles.optionsContainer}>
                            {(["leaderboardSegueLinha", "leaderboardVelocidade", "leaderboardFinal", "sponsors"] as LeaderboardMode[]).map((mode) => (
                                <Card 
                                    key={mode} 
                                    elevation={selectedMode === mode ? 4 : 1}
                                    className={`${styles.optionCard} ${selectedMode === mode ? styles.selectedCard : ''}`}
                                >
                                    <CardActionArea 
                                        onClick={() => setSelectedMode(mode)}
                                        className={styles.cardAction}
                                    >
                                        <CardContent className={styles.cardContent}>
                                            <Box className={`${styles.modeIconContainer} ${selectedMode === mode ? styles.selectedIcon : ''}`}>
                                                {getModeIcon(mode)}
                                            </Box>
                                            <Typography variant="h6" gutterBottom>
                                                {getModeName(mode)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {getModeDescription(mode)}
                                            </Typography>
                                            
                                            {currentDisplayedMode === mode && (
                                                <Chip 
                                                    label="Ativo" 
                                                    color="primary" 
                                                    size="small"
                                                    className={styles.activeChip}
                                                />
                                            )}
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>

                        <Box className={styles.actionsContainer}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSave}
                                disabled={saving || selectedMode === currentDisplayedMode}
                                className={styles.saveButton}
                                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                            >
                                {saving ? "Aplicando..." : "Aplicar Mudanças"}
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LeaderboardController;
